import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  getBills,
  createOrAddToBill,
  splitBill,
  splitAndPayBill,
  updateItemStatus,
  updateItemServed,
  applyDiscount,
  removeDiscount,
  applyItemDiscounts,
  applyAmountDiscount,
  setItemPrices,
  cancelAllBillItems,
  restoreBill,
  closeBillRetry,
  renameBill,
  transferBillWaiter,
  transferAllBillsWaiter,
  getTakenTables,
  getWaiterOpenCounts,
  getWaitersByTurnover,
  printReceiptCopy,
} from "../api/bills";
import { cancelBillItems, type CancelBillItemsIn } from "../api/billsCancel";
import { changeKitchenHold, releaseBillElement } from "../api/billKitchenHold";
import { getFiscalIssues, retryStorno } from "../api/billsFiscalIssues";
import { hasFiscalDocument } from "../lib/fiscalInvoice";
import type {
  CreateBillIn,
  SplitBillIn,
  SplitAndPayBillIn,
  UpdateItemStatusIn,
  UpdateItemServedIn,
  ApplyDiscountIn,
  ApplyItemDiscountsIn,
  SetItemPricesIn,
  CancelAllItemsIn,
  RestoreBillIn,
  RetryCloseBillIn,
} from "../api/bills";
import { useAppSelector } from "../store/hooks";

interface UseBillsOptions {
  /** Само от кеша: без четене при монтиране, без 10-секундния интервал и без
   *  четене при фокус — за помощниците под `Ordering` (футърът, „Клиенти",
   *  гласът, „Смяна на сервитьор"). */
  fromCacheOnly?: boolean;
}

// Управляващият абонат на сметките е САМО `Ordering` (useBills() без опции):
// той чете при монтиране, на 10 s при резерв и при фокус. Помощниците, монтирани
// по-късно (след менюто, при избор на маса, в продуктовия изглед), пускаха всеки
// свой GET /bills с данни отпреди миг (staleTime 0). Те получават същите данни
// от кеша при всяка промяна, а мутациите (invalidate) и refreshBills продължават
// да теглят по веднъж — заявката е активна през `Ordering`.
export const useBills = ({ fromCacheOnly = false }: UseBillsOptions = {}) => {
  // Локален резерв (docs/edge.md): при включен резерв масите се опресняват
  // на 10 s И В ДВАТА РЕЖИМА — в облака всеки отговор се огледва към касата,
  // за да е списъкът ѝ пресен при отпадане на интернета; в локален режим
  // касата е единственият източник и другите терминали се виждат само така.
  const edgeEnabled = useAppSelector((state) => state.edge.enabled);
  return useQuery({
    queryKey: ["bills"],
    queryFn: getBills,
    staleTime: 0,
    refetchInterval: edgeEnabled ? 10_000 : false,
    enabled: !fromCacheOnly,
  });
};

// Свежите сметки по действие на сервитьора (клик на маса, връщане към масите):
// тегли ВСЕКИ път, но заявка, която вече е в полет, се ползва, вместо да се
// отменя и да тръгне нова. С invalidate след „Поръчай" отиваха два GET /bills
// (мутацията + връщането към масите), а при бързи кликове — по един на клик;
// сървърът изчисляваше всичките, а ползваше се само последният.
export const refreshBills = (queryClient: QueryClient) =>
  queryClient.refetchQueries({ queryKey: ["bills"], exact: true }, { cancelRefetch: false });

// Проблемни фискализации — опреснява се периодично: късен резултат от вече
// отпечатан бон може да реши проблема пасивно (сметката изчезва сама).
export const useFiscalIssues = (days = 7) => {
  return useQuery({
    queryKey: ["fiscal-issues", days],
    queryFn: () => getFiscalIssues(days),
    refetchInterval: 60_000,
  });
};

export const useCreateBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBillIn) => createOrAddToBill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Сторно от Маркиране — всички избрани редове с ЕДНА заявка (един кухненски
// билет на станция); стар сървър → ред по ред (`api/billsCancel.ts`).
export const useCancelBillItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: CancelBillItemsIn;
    }) => cancelBillItems(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useSplitBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: SplitBillIn }) =>
      splitBill(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useSplitAndPayBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: SplitAndPayBillIn;
    }) => splitAndPayBill(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// „Пусни сега" за задържана поръчка — тя влиза в кухнята веднага. Вече освободена
// (таймерът, друг терминал) BE връща 200 със същия документ, затова
// инвалидацията е и „тихото опресняване" (tables/kitchenHold/). Връща
// обещанието: `mutateAsync` приключва чак с новите сметки — дотогава бутонът
// е зает и второ натискане не праща втора заявка.
export const useReleaseBillElement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, elementId }: { billId: number; elementId: number }) =>
      releaseBillElement(billId, elementId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bills"] }),
  });
};

// „Промени" — новото време на задържана поръчка (`api/billKitchenHold.ts`);
// инвалидацията носи новия срок и на брояча, и на опресняването при изтичане.
export const useChangeKitchenHold = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      elementId,
      minutes,
    }: {
      billId: number;
      elementId: number;
      minutes: number | null;
    }) => changeKitchenHold(billId, elementId, minutes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bills"] }),
  });
};

export const useUpdateItemStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateItemStatusIn) => updateItemStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useUpdateItemServed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateItemServedIn) => updateItemServed(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useApplyBillDiscount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: ApplyDiscountIn }) =>
      applyDiscount(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useRemoveBillDiscount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (billId: number) => removeDiscount(billId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useApplyItemDiscounts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: ApplyItemDiscountsIn;
    }) => applyItemDiscounts(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useApplyAmountDiscount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, amount }: { billId: number; amount: string }) =>
      applyAmountDiscount(billId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useSetItemPrices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: SetItemPricesIn }) =>
      setItemPrices(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Hook за повторно затваряне на сметка (retry) - за закрити сметки без total
export const useRenameBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, name }: { billId: number; name: string }) =>
      renameBill(billId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useTransferBillWaiter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      waiterId,
      allowDuplicateName,
    }: {
      billId: number;
      waiterId: number;
      allowDuplicateName?: boolean;
    }) => transferBillWaiter(billId, waiterId, allowDuplicateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

// Брой отворени маси по сервитьор — само докато модалът за прехвърляне е
// отворен. В локален режим касата не сервира този път (§14.6 №3 на договора):
// три напразни опита биха забавили модала, а броячите остават празни — точно
// както при провал, само че без чакане.
export const useWaiterOpenCounts = (enabled: boolean) => {
  const edgeLocal = useAppSelector((state) => state.edge.mode === "local");
  return useQuery({
    queryKey: ["bills", "waiter-open-counts"],
    queryFn: getWaiterOpenCounts,
    enabled: enabled && !edgeLocal,
    staleTime: 0,
  });
};

// Подредбата на сервитьорите в избора на сервитьор на „Нова маса" — само докато
// модалът е отворен; касата в локален режим няма този адрес (изборът се пропуска).
export const useWaitersByTurnover = (enabled: boolean) => {
  const edgeLocal = useAppSelector((state) => state.edge.mode === "local");
  return useQuery({
    queryKey: ["bills", "waiters-by-turnover"],
    queryFn: getWaitersByTurnover,
    enabled: enabled && !edgeLocal,
    staleTime: 0,
  });
};

export const TAKEN_TABLES_KEY = ["bills", "taken-tables"] as const;

// Заетите маси на колегите, които влезлият не вижда (режим „Схема" при обща
// номерация без „Настани"). Под ["bills"] — мутациите по сметките
// (invalidate) го опресняват; иначе при клик по маса (`refreshFloor`
// в `useOrderingFloor`), без интервал и без фокус — като схемата. В локален
// режим касата не сервира този път (503).
export const useTakenTables = (enabled: boolean) => {
  const edgeLocal = useAppSelector((state) => state.edge.mode === "local");
  return useQuery({
    queryKey: TAKEN_TABLES_KEY,
    queryFn: getTakenTables,
    enabled: enabled && !edgeLocal,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useTransferAllBillsWaiter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      waiterId,
      allowDuplicateName,
    }: {
      waiterId: number;
      allowDuplicateName?: boolean;
    }) => transferAllBillsWaiter(waiterId, allowDuplicateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    },
  });
};

export const useCloseBillRetry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      ...data
    }: { billId: number } & RetryCloseBillIn) => closeBillRetry(billId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
      // Повторението на бон с фискална фактура я печата от записания payload —
      // нов запис във „Фактури" (или незаписан документ в „Фискални проблеми");
      // ["bills"] по-горе хваща и ["bills", "uninvoiced"].
      if (hasFiscalDocument(result)) {
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
      }
    },
  });
};

// Повторно сторно (кредитно известие) за ред от storno_issues — виж
// retryStorno в api/billsFiscalIssues.ts за request_id правилото.
export const useStornoRetry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId }: { billId: number }) => retryStorno(billId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-issues"] });
      // Сторно на сметка с фискална фактура — кредитното известие е нов запис.
      if (hasFiscalDocument(result)) {
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
      }
    },
  });
};

export const useCancelAllBillItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: CancelAllItemsIn;
    }) => cancelAllBillItems(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
      // BE анулира издадената фактура на сметката в същата операция;
      // фискалната — не: издава кредитно известие към нея (нов запис).
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

export const useRestoreBill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      billId,
      data,
    }: {
      billId: number;
      data: RestoreBillIn;
    }) => restoreBill(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
      // BE анулира издадената фактура на сметката в същата операция;
      // фискалната — не: издава кредитно известие към нея (нов запис).
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

/**
 * Печат на копие от фискалния бон на затворена сметка.
 *
 * Нищо не се инвалидира — копието не променя сметката, а `isPending` на
 * мутацията е и защитата от двойно натискане: бутонът стои disabled, докато
 * заявката е в полет, тоест втори печат не може да тръгне от бързо второ
 * кликане.
 */
export const usePrintReceiptCopy = () => {
  return useMutation({
    mutationFn: ({ billId }: { billId: number }) => printReceiptCopy(billId),
  });
};
